import { useEffect } from 'react';
import { useAuth as useClerkAuth, useOrganization, useSession } from '@clerk/react';
import { useLocation, useNavigate } from 'react-router-dom';

import { readPendingCompanyName, readPendingTenantId } from '@/api/tenants/linkClerkOrg';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';
import {
  isClerkAuthFlowPath,
  isClerkAuthLandingPath,
  isClerkChooseOrganizationTaskPath,
  isClerkHostedAuthSubRoute,
} from '@/utils/auth/clerkAuthPaths';
import { isPathBCustomOrgConfirmEnabled } from '@/utils/auth/pathBOrgConfirm';
import { AppRoutes } from '@/utils/routes/router';

const LOG = '[ChurnSignal][PathB-Org]';

function hasPathBPendingRegistration(): boolean {
  return Boolean(readPendingTenantId() || readPendingCompanyName());
}

function chooseOrganizationPathFor(pathname: string): string {
  if (pathname.startsWith(AppRoutes.signUp) || hasPathBPendingRegistration()) {
    return `${AppRoutes.signUp}/tasks/choose-organization`;
  }
  return `${AppRoutes.signIn}/tasks/choose-organization`;
}

/**
 * After OTP, keep users in the Clerk auth flow until an organization exists.
 * Once an org exists (including while still on /tasks/choose-organization), go to dashboard.
 */
function ClerkSignedInRedirectInner() {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { session, isLoaded: isSessionLoaded } = useSession();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoaded || !isSessionLoaded) {
      console.info(LOG, 'ClerkSignedInRedirect wait', {
        pathname: location.pathname,
        isLoaded,
        isSignedIn,
        isOrgLoaded,
        isSessionLoaded,
      });
      return;
    }

    const pendingTask = session?.currentTask?.key ?? null;
    const chooseOrgPath = hasPathBPendingRegistration()
      ? `${AppRoutes.signUp}/tasks/choose-organization`
      : chooseOrganizationPathFor(location.pathname);
    const onChooseOrg = isClerkChooseOrganizationTaskPath(location.pathname);

    // Pending org task wins even if isSignedIn flickers false after a dropped navigate.
    if (pendingTask === 'choose-organization' && !onChooseOrg) {
      console.info(LOG, 'ClerkSignedInRedirect → choose-organization (session.currentTask)', {
        pathname: location.pathname,
        target: chooseOrgPath,
        isSignedIn,
      });
      navigate(chooseOrgPath, { replace: true });
      return;
    }

    if (!isSignedIn || !isOrgLoaded) {
      return;
    }

    const pathB = hasPathBPendingRegistration();
    const customOrgConfirm = isPathBCustomOrgConfirmEnabled();
    const onSignInOrUp = isClerkAuthFlowPath(location.pathname);
    const onHostedSubRoute = isClerkHostedAuthSubRoute(location.pathname);

    console.info(LOG, 'ClerkSignedInRedirect decide', {
      pathname: location.pathname,
      organizationId: organization?.id ?? null,
      pendingTask,
      pathB,
      customOrgConfirm,
      onSignInOrUp,
      onHostedSubRoute,
      onChooseOrg,
    });

    // Org created — leave choose-org / auth landing for the app (do this before the
    // "do not interrupt" guard, which previously left an empty TaskChooseOrganization).
    if (organization) {
      if (onChooseOrg || isClerkAuthLandingPath(location.pathname)) {
        console.info(LOG, 'ClerkSignedInRedirect → dashboard (has org)', {
          pathname: location.pathname,
        });
        navigate(AppRoutes.dashboard, { replace: true });
      }
      return;
    }

    if (pathB && customOrgConfirm) {
      if (!onSignInOrUp && location.pathname !== chooseOrgPath) {
        console.info(LOG, 'ClerkSignedInRedirect → choose-organization (Path B custom)', {
          target: chooseOrgPath,
        });
        navigate(chooseOrgPath, { replace: true });
      }
      return;
    }

    // OTP / MFA / in-progress choose-organization — do not interrupt.
    if (onHostedSubRoute || onChooseOrg) {
      return;
    }

    if (location.pathname === chooseOrgPath) {
      return;
    }
    console.info(LOG, 'ClerkSignedInRedirect → choose-organization (no active org)', {
      pathname: location.pathname,
      target: chooseOrgPath,
    });
    navigate(chooseOrgPath, { replace: true });
  }, [
    isLoaded,
    isSignedIn,
    isOrgLoaded,
    isSessionLoaded,
    organization?.id,
    session,
    location.pathname,
    navigate,
  ]);

  return null;
}

export default function ClerkSignedInRedirect() {
  if (!isClerkAuthEnabled()) {
    return null;
  }

  return <ClerkSignedInRedirectInner />;
}
